// 16 formes d'équipes (doc 05 §3.3) — la couleur n'est JAMAIS seule : chaque équipe
// porte couleur + forme + nom/avatar. Monochromes, viewBox 24, `fill="currentColor"`,
// silhouettes pleines lisibles à 2.4vh sur une TV (pas de trait fin qui disparaît).
// Source de vérité : ce fichier. `scripts/build-assets.ts` en dérive
// `assets/shapes/<shape>.svg` (16 fichiers, doc 05 §9.1).

import { teamColors } from './palette.js';

export interface ShapeDef {
  /** Clé de forme, alignée sur `teamColors[].shape`. */
  key: string;
  /** Contenu du `<svg viewBox="0 0 24 24">` — `fill="currentColor"` porté par le svg. */
  body: string;
}

export const shapes = [
  { key: 'circle', body: '<circle cx="12" cy="12" r="9"/>' },
  { key: 'triangle', body: '<path d="M12 3 21.5 19.5H2.5Z"/>' },
  {
    key: 'star',
    body: '<path d="M12 2.5 14.9 8.9 21.9 9.7 16.7 14.4 18.1 21.3 12 17.8 5.9 21.3 7.3 14.4 2.1 9.7 9.1 8.9Z"/>',
  },
  { key: 'lozenge', body: '<path d="M12 2.5 19 12 12 21.5 5 12Z"/>' },
  { key: 'square', body: '<rect x="3.5" y="3.5" width="17" height="17" rx="2"/>' },
  {
    key: 'drop',
    body: '<path d="M12 2.5c4 4.6 6.5 7.9 6.5 11a6.5 6.5 0 0 1-13 0c0-3.1 2.5-6.4 6.5-11Z"/>',
  },
  { key: 'pentagon', body: '<path d="M12 2.5 21.5 9.4 17.9 20.6H6.1L2.5 9.4Z"/>' },
  { key: 'cross', body: '<path d="M9.4 2.5h5.2v6.9h6.9v5.2h-6.9v6.9H9.4v-6.9H2.5V9.4h6.9Z"/>' },
  {
    key: 'crescent',
    body: '<path d="M18.6 17.6A9.5 9.5 0 1 1 15.4 3a7.6 7.6 0 0 0 3.2 14.6Z"/>',
  },
  { key: 'hexagon', body: '<path d="M12 2.5 20.2 7.25v9.5L12 21.5 3.8 16.75v-9.5Z"/>' },
  { key: 'bolt', body: '<path d="M13.8 2 4.5 13.6h5.6L9.4 22l9.6-12h-6.1Z"/>' },
  { key: 'diamond', body: '<path d="M12 2.2 21.8 8.6 12 21.8 2.2 8.6Z"/>' },
  {
    key: 'spark',
    body: '<path d="M12 2c.9 4.9 2.4 7.7 6.8 8.6-4.4.9-5.9 3.7-6.8 8.6-.9-4.9-2.4-7.7-6.8-8.6C9.6 9.7 11.1 6.9 12 2Z"/><circle cx="18.6" cy="18.4" r="2.4"/>',
  },
  {
    key: 'heart',
    body: '<path d="M12 21S2.8 15 2.8 8.9A5.4 5.4 0 0 1 12 5.2a5.4 5.4 0 0 1 9.2 3.7C21.2 15 12 21 12 21Z"/>',
  },
  {
    key: 'ring',
    body: '<path d="M12 2.5A9.5 9.5 0 1 0 12 21.5 9.5 9.5 0 0 0 12 2.5Zm0 5a4.5 4.5 0 1 1 0 9 4.5 4.5 0 0 1 0-9Z"/>',
  },
  {
    key: 'wave',
    body: '<path d="M2 8.4c2.5-3 5-3 7.5 0s5 3 7.5 0 3.5-2.4 5 0v3.2c-1.5-2.4-2.5-3-5 0s-5 3-7.5 0-5-3-7.5 0Zm0 7c2.5-3 5-3 7.5 0s5 3 7.5 0 3.5-2.4 5 0v3.2c-1.5-2.4-2.5-3-5 0s-5 3-7.5 0-5-3-7.5 0Z"/>',
  },
] as const satisfies readonly ShapeDef[];

export type ShapeKey = (typeof shapes)[number]['key'];

const shapeIndex: Record<string, string> = {};
for (const shape of shapes) {
  shapeIndex[shape.key] = shape.body;
}

export function shapeBody(key: string): string {
  return shapeIndex[key] ?? shapeIndex['circle'] ?? '';
}

/** Forme associée à une couleur d'équipe (hex de la palette fermée). */
export function shapeForTeamHex(hex: string): ShapeKey {
  const upper = hex.toUpperCase();
  const found = teamColors.find((c) => c.hex.toUpperCase() === upper);
  return (found?.shape ?? 'circle') as ShapeKey;
}
